/**
 * Main SDK class providing access to protocol-specific decoding modules.
 *
 * @example
 * ```ts
 * import { DecodingSDK } from "@blockdaemon/transaction-verifier";
 *
 * const sdk = new DecodingSDK({ logLevel: "info" });
 *
 * // Access Ethereum decoding module
 * const eth = sdk.ethereumDecodingModule;
 * const decoded = eth.decodeStakewiseTransaction(calldataHex);
 *
 * // Access Solana decoding module
 * const sol = sdk.solanaDecodingModule;
 * const summary = sol.summarizeUnsignedTransaction(b64Tx);
 * ```
 *
 * @module
 */
import { EthereumDecodingModule } from "./modules/ethereum/ethereum-decoding-module.js";
import { SolanaDecodingModule } from "./modules/solana/solana-decoding-module.js";
/**
 * Main entry point for class-based SDK usage.
 *
 * Provides access to protocol-specific decoding modules. Currently supports
 * Ethereum with Stakewise vault transaction decoding and verification, and
 * Solana with staking transaction decoding and validation.
 *
 * @example
 * ```ts
 * const sdk = new DecodingSDK({ logLevel: "info" });
 *
 * // Decode and verify a Stakewise staking transaction
 * const eth = sdk.ethereumDecodingModule;
 * const result = eth.stakewiseVerifyStakeUnsignedTx(
 *   { assets: 1000000000000000000n, userAddress: "0x...", chainId: "1" },
 *   { to: vaultAddress, data: calldata, value: 1000000000000000000n },
 *   { expectedChainId: "1" }
 * );
 *
 * // Validate a Solana staking transaction
 * const sol = sdk.solanaDecodingModule;
 * const validation = sol.validateUnsignedTransaction(b64Tx, {
 *   requireDelegate: true,
 *   forbidAuthorize: true,
 * });
 * ```
 */
export class DecodingSDK {
    /**
     * Ethereum decoding module for EVM transaction decoding and Stakewise verification.
     */
    ethereumDecodingModule;
    /**
     * Solana decoding module for Solana staking transaction decoding and validation.
     */
    solanaDecodingModule;
    constructor(config) {
        this.ethereumDecodingModule = new EthereumDecodingModule({
            logLevel: config.logLevel,
            sharesTolerancePercent: config.sharesTolerancePercent,
        });
        this.solanaDecodingModule = new SolanaDecodingModule({ logLevel: config.logLevel });
    }
}
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoic2RrLmpzIiwic291cmNlUm9vdCI6IiIsInNvdXJjZXMiOlsiLi4vLi4vLi4vc3JjL21haW4vc2RrLnRzIl0sIm5hbWVzIjpbXSwibWFwcGluZ3MiOiJBQUFBOzs7Ozs7Ozs7Ozs7Ozs7Ozs7O0dBbUJHO0FBRUgsT0FBTyxFQUFFLHNCQUFzQixFQUFFLE1BQU0sZ0RBQWdELENBQUM7QUFDeEYsT0FBTyxFQUFFLG9CQUFvQixFQUFFLE1BQU0sNENBQTRDLENBQUM7QUFvQmxGOzs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7OztHQTBCRztBQUNILE1BQU0sT0FBTyxXQUFXO0lBQ3RCOztPQUVHO0lBQ2Esc0JBQXNCLENBQXlCO0lBRS9EOztPQUVHO0lBQ2Esb0JBQW9CLENBQXVCO0lBRTNELFlBQVksTUFBa0M7UUFDNUMsSUFBSSxDQUFDLHNCQUFzQixHQUFHLElBQUksc0JBQXNCLENBQUM7WUFDdkQsUUFBUSxFQUFFLE1BQU0sQ0FBQyxRQUFRO1lBQ3pCLHNCQUFzQixFQUFFLE1BQU0sQ0FBQyxzQkFBc0I7U0FDdEQsQ0FBQyxDQUFDO1FBQ0gsSUFBSSxDQUFDLG9CQUFvQixHQUFHLElBQUksb0JBQW9CLENBQUMsRUFBRSxRQUFRLEVBQUUsTUFBTSxDQUFDLFFBQVEsRUFBRSxDQUFDLENBQUM7SUFDdEYsQ0FBQztDQUNGIn0=